import { Injectable, inject } from '@angular/core';
import { ENVIRONMENT, InputFileService, ModalService } from '@cfp/core';
import { LoadingModal } from '@cfp/ui/feedback';
import { TranslateService } from '@ngx-translate/core';
import { GeneratePdfRequestDTO, LabelGroupDTO, PDFService, PdfReportTypeDTO } from 'csr-asset-app';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, Subscription } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PdfService {
  private bsModalRef: BsModalRef<LoadingModal>;

  private readonly inputFileService = inject(InputFileService);
  private readonly environment = inject(ENVIRONMENT);
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly PDFService = inject(PDFService);

  /**
   * Get a PDF generated from the html page specified in the url
   */
  public getPdfByUrl(routerUrl: string, labelGroup?: LabelGroupDTO): Observable<any> {
    const timezone: string = encodeURI(Intl.DateTimeFormat().resolvedOptions().timeZone);
    return this.inputFileService.getBlob(
      `${this.environment.apiV2}pdf?environmentCode=${this.environment.environmentCode.path}&path=/csr/app/${this.environment.environmentCode.path}${routerUrl}&language=${this.translateService.currentLang}&timezone=${timezone}${labelGroup ? '&labelGroup=' + labelGroup : ''}`
    );
  }

  public isLoading(): boolean {
    return this.bsModalRef?.content?.loading() || false;
  }

  /**
   * Download a generated PDF report for the specified router url and presents the pdf as download with the specified file name
   * Also, sends the pdf as e-mail attachment to the configured address
   * @param fileName
   */
  public downloadAndSendAsEmailAttachment(
    routerUrl: string,
    fileName: string,
    buildingId: number,
    labelGroup?: string
  ): void {
    const timezone: string = encodeURI(Intl.DateTimeFormat().resolvedOptions().timeZone);
    let pdfObservable = this.inputFileService.getBlob(
      `${this.environment.apiV2}pdf-with-email?environmentCode=${this.environment.environmentCode.path}&path=/csr/app/${this.environment.environmentCode.path}${routerUrl}&language=${this.translateService.currentLang}&timezone=${timezone}${labelGroup ? '&labelGroup=' + labelGroup : ''}&buildingId=${buildingId}`
    );
    this.downloadPdf(fileName, pdfObservable);
  }

  /**
   * Download a generated PDF report for the specified router url and presents the pdf as download with the specified file name
   * @param routerUrl
   * @param fileName
   */
  public download(routerUrl: string, fileName: string, labelGroup?: LabelGroupDTO): void {
    let pdfObservable = this.getPdfByUrl(routerUrl, labelGroup);
    this.downloadPdf(fileName, pdfObservable);
  }

  private downloadPdf(fileName: string, pdfObservable: Observable<any>): void {
    this.bsModalRef = this.modalService.show(LoadingModal, {
      class: 'modal-sm modal-dialog-centered',
      ignoreBackdropClick: true
    });

    let getPdfSubscription: Subscription;
    this.bsModalRef.content.loadingText.set('print.report');
    this.bsModalRef.content.loadingSubText.set('print.report.subText');
    this.bsModalRef.content.loading.set(true);
    this.bsModalRef.content.onClose.subscribe((result: boolean) => {
      if (!result && getPdfSubscription) {
        getPdfSubscription.unsubscribe();
        getPdfSubscription = undefined;
      }
      this.bsModalRef.hide();
      this.bsModalRef = undefined;
    });

    getPdfSubscription = pdfObservable.subscribe({
      next: (response: any) => {
        const fileNameWithExtenson = `${fileName}.pdf`;

        if (window.navigator && (window.navigator as any).msSaveBlob) {
          (window.navigator as any).msSaveBlob(response.body, fileNameWithExtenson);
        } else {
          let downloadLink = document.createElement('a');
          downloadLink.href = window.URL.createObjectURL(response.body);
          downloadLink.download = fileNameWithExtenson;
          downloadLink.target = '_blank';
          downloadLink.tabIndex = -1;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          downloadLink.remove();
        }

        this.bsModalRef.content.message.set('print.report.success');
        this.bsModalRef.content.success.set(true);
        this.bsModalRef.content.loading.set(false);
      },
      error: (error) => {
        this.bsModalRef.content.message.set('print.report.error');
        this.bsModalRef.content.success.set(false);
        this.bsModalRef.content.loading.set(false);
        console.log('PDF error', error);
      }
    });
  }

  public downloadByPublicUrl(buildingId?: number, labelGroup?: LabelGroupDTO): void {
    const timezone: string = encodeURI(Intl.DateTimeFormat().resolvedOptions().timeZone);
    let language = this.translateService.currentLang;
    const generatePdfRequest: GeneratePdfRequestDTO = {
      environmentCode: this.environment.environmentCode.dto,
      reportType: PdfReportTypeDTO.BUILDINGLITE,
      labelGroup: labelGroup,
      language: language,
      buildingId: buildingId,
      sendEmail: true,
      timezone: timezone
    };

    this.bsModalRef = this.modalService.show(LoadingModal, {
      class: 'modal-sm modal-dialog-centered',
      ignoreBackdropClick: true
    });

    this.bsModalRef.content.loadingText.set('print.report');
    this.bsModalRef.content.loadingSubText.set('print.report.subText');
    this.bsModalRef.content.loading.set(true);
    this.bsModalRef.content.onClose.subscribe((result: boolean) => {
      this.bsModalRef.hide();
      this.bsModalRef = undefined;
    });

    this.PDFService.v2PdfGeneratePost(generatePdfRequest).subscribe({
      next: (generateResponse) => {
        if (this.bsModalRef) {
          this.bsModalRef.content.message.set('print.report.success');
          this.bsModalRef.content.success.set(true);
          this.bsModalRef.content.loading.set(false);
          this.bsModalRef.content.downloadUrl.set(`${this.environment.apiV1}pdf-download?id=${generateResponse.id}`);
        }
      },
      error: (error) => {
        this.bsModalRef.content.message.set('print.report.error');
        this.bsModalRef.content.success.set(false);
        this.bsModalRef.content.loading.set(false);
        console.log('PDF error', error);
      }
    });
  }
}
